import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { translate } from '../../utils/i18nHelper';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_ADMIN_APPS_LIST,
  API_ADMIN_APPS_CREATE,
  API_ADMIN_APPS_GET,
  API_ADMIN_APPS_UPDATE,
  API_ADMIN_APPS_DELETE,
  API_ADMIN_APPS_SET_ACTIVE,
  API_SYSAPPS_AVAILABLE,
} from '../../constants/apiConstants';

export interface SysAppDto {
  id: number;
  guid: string;
  appCode: string;
  appName: string;
  classification: string;
  /**
   * Access gating type. `'public'` = available to every tenant; `'private'`
   * = subscription-gated. Applies uniformly to apps, tools, AND modules per
   * the edi-module-registration spec. `null` is legacy / unset.
   */
  accessType: string | null;
  /** Storage scope for data owned by this app/tool/module. */
  storageScope: string | null;
  description: string | null;
  iconUrl: string | null;
  routeUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  status: string;
  createdAt: string | null;
  modifiedAt: string | null;
  /**
   * Optional parent-module `app_code` (e.g. `'edi'`). `null` for module
   * rows themselves and for stand-alone apps/tools. Used by FE selectors
   * to derive module membership without a second round-trip.
   */
  moduleCode: string | null;
}

export interface CreateSysAppRequest {
  appCode: string;
  appName: string;
  classification: string;
  /** Access gating type ('public' | 'private'). Must be null for module-classified entries. */
  accessType: string | null;
  storageScope: string | null;
  description: string | null;
  iconUrl: string | null;
  routeUrl: string | null;
  sortOrder: number;
}

export interface UpdateSysAppRequest {
  appName: string;
  classification: string;
  /** Access gating type ('public' | 'private'). Must be null for module-classified entries. */
  accessType: string | null;
  storageScope: string | null;
  description: string | null;
  iconUrl: string | null;
  routeUrl: string | null;
  sortOrder: number;
}

export interface SysAppListResponse {
  apps: SysAppDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Map backend status codes to i18n keys
const STATUS_CODE_I18N: Record<number, string> = {
  [-702]: 'admin.appRegistry.error.alreadyExists',
};

function getLocalizedError(response: any): string {
  const code = response?.statusCode;
  if (code && STATUS_CODE_I18N[code]) {
    return translate(STATUS_CODE_I18N[code]);
  }
  return response?.messages?.join(', ') || 'Unknown error';
}

export const sysAppService = {
  async getApps(page: number, pageSize: number): Promise<SysAppListResponse> {
    try {
      const response = await apiClient.get(API_ADMIN_APPS_LIST, { params: { page, pageSize } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch apps', error);
      toast.error(`Failed to load apps: ${msg}`);
      throw error;
    }
  },

  async getApp(id: number): Promise<SysAppDto> {
    try {
      const response = await apiClient.get(API_ADMIN_APPS_GET(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data.app;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch app', error, { id });
      toast.error(`Failed to load app: ${msg}`);
      throw error;
    }
  },

  async createApp(request: CreateSysAppRequest): Promise<{ appId: number; appCode: string }> {
    try {
      const response = await apiClient.post(API_ADMIN_APPS_CREATE, request);
      if (!response.data.succeeded) throw new Error(getLocalizedError(response.data));
      toast.success(translate('admin.appRegistry.success.created'));
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to create app', error);
      toast.error(msg);
      throw error;
    }
  },

  async updateApp(id: number, request: UpdateSysAppRequest): Promise<void> {
    try {
      const response = await apiClient.put(API_ADMIN_APPS_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(getLocalizedError(response.data));
      toast.success(translate('admin.appRegistry.success.updated'));
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to update app', error, { id });
      toast.error(msg);
      throw error;
    }
  },

  async deleteApp(id: number): Promise<void> {
    try {
      const response = await apiClient.delete(API_ADMIN_APPS_DELETE(id));
      if (!response.data.succeeded) throw new Error(getLocalizedError(response.data));
      toast.success(translate('admin.appRegistry.success.deleted'));
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to delete app', error, { id });
      toast.error(msg);
      throw error;
    }
  },

  /**
   * Returns the set of apps/tools visible to the current user.
   * Module-classified entries are excluded by the backend.
   * Calls GET /api/v1/sysapps/available (authenticated, no admin policy).
   * Pass tenantId to include tenant-subscribed apps alongside public/personal apps.
   */
  async getAvailableApps(tenantId?: number): Promise<SysAppDto[]> {
    try {
      const params = tenantId !== undefined ? { tenantId } : undefined;
      const response = await apiClient.get(API_SYSAPPS_AVAILABLE, { params });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data.apps ?? [];
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch available apps', error);
      throw new Error(msg);
    }
  },

  /**
   * Same as getAvailableApps but also returns workspace config flags.
   * Used by the workspace page to determine whether to render a custom view.
   */
  async getAvailableAppsWithConfig(tenantId: number): Promise<{ apps: SysAppDto[]; useCustomWorkspace: boolean; tenantCompanyName: string | null; tenantLogoUrl: string | null; tenantLogoIconUrl: string | null }> {
    try {
      const response = await apiClient.get(API_SYSAPPS_AVAILABLE, { params: { tenantId } });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return {
        apps: response.data.data.apps ?? [],
        useCustomWorkspace: response.data.data.useCustomWorkspace ?? false,
        tenantCompanyName: response.data.data.tenantCompanyName ?? null,
        tenantLogoUrl: response.data.data.tenantLogoUrl ?? null,
        tenantLogoIconUrl: response.data.data.tenantLogoIconUrl ?? null,
      };
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to fetch available apps with config', error);
      throw new Error(msg);
    }
  },

  async setActive(id: number, isActive: boolean): Promise<void> {
    try {
      const response = await apiClient.patch(API_ADMIN_APPS_SET_ACTIVE(id), { isActive });
      if (!response.data.succeeded) throw new Error(getLocalizedError(response.data));
      toast.success(translate(isActive ? 'admin.appRegistry.success.activated' : 'admin.appRegistry.success.deactivated'));
    } catch (error) {
      const msg = getErrorMessage(error);
      logger.error('Failed to set app active status', error, { id, isActive });
      toast.error(msg);
      throw error;
    }
  },
};
